import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
} from '@mui/material';
import Api from '../../../../Services/Api';
import { fetchPreloadedImages, generateAdCopy } from '../utils/openaiUtils';
import AccountSelector from './AccountSelector';
import PageSelector from './PageSelector';

const quickSteps = [
  'Select Account',
  'Select Pages',
  'Configure Categories',
  'Launch Campaigns'
];

// Special Ad Categories options (same as manual launcher)
const specialAdCategories = [
  { value: 'NONE', label: 'None', description: 'No special category' },
  { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Financial Products & Services', description: 'Ads related to credit cards, loans, insurance, investment advice, or other financial services' },
  { value: 'EMPLOYMENT', label: 'Employment', description: 'Ads related to job opportunities or employment' },
  { value: 'HOUSING', label: 'Housing', description: 'Ads related to housing, real estate, or mortgage' },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Social issues, elections or politics', description: 'Ads about social issues, elections, or politics' },
];

const QuickLauncher = ({ 
  facebookAccounts, 
  accountsLoading, 
  accountsError, 
  onError, 
  onSuccess 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);
  
  // Quick launch data
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [pageCategories, setPageCategories] = useState({}); // Store special categories per page
  const [campaignProgress, setCampaignProgress] = useState([]);
  const [optimalPageId, setOptimalPageId] = useState(null);
  const [optimalPageName, setOptimalPageName] = useState(null);
  const [pageLoadStats, setPageLoadStats] = useState({});
  const [loadingOptimalPage, setLoadingOptimalPage] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);


  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    handleNext();
  };

  const handlePageSelect = (pages) => {
    setSelectedPages(pages);
    // Don't auto-advance - let user select multiple pages and click Next
  };

  const handleContinueWithPages = () => {
    if (selectedPages.length === 0) {
      onError('Please select at least one page to launch campaigns for.');
      return;
    }
    // Initialize categories for selected pages (use rsoc_spy special_ad_categories or default to NONE)
    const initialCategories = {};
    selectedPages.forEach(page => {
      // Use special_ad_categories from rsoc_spy if available, otherwise default to NONE
      if (page.special_ad_categories && Array.isArray(page.special_ad_categories) && page.special_ad_categories.length > 0) {
        initialCategories[page.id] = page.special_ad_categories;
      } else {
        initialCategories[page.id] = ['NONE'];
      }
    });
    setPageCategories(initialCategories);
    handleNext();
  };

  const handleCategoriesChange = (pageId, newCategories) => {
    console.log('Categories change for page', pageId, ':', newCategories);
    
    // Handle special logic for "NONE" category
    let finalCategories = [...newCategories];
    
    // If "NONE" was just selected and there were other categories
    const previousCategories = pageCategories[pageId] || ['NONE'];
    const noneWasAdded = newCategories.includes('NONE') && !previousCategories.includes('NONE');
    const otherWasAdded = !newCategories.includes('NONE') && previousCategories.includes('NONE') && newCategories.length > 0;
    
    if (noneWasAdded && newCategories.length > 1) {
      // User selected "NONE" while other categories exist, keep only NONE
      finalCategories = ['NONE'];
    } else if (otherWasAdded) {
      // User selected a category while "NONE" was selected, remove NONE
      finalCategories = newCategories.filter(cat => cat !== 'NONE');
    } else if (newCategories.includes('NONE') && newCategories.length > 1) {
      // Remove NONE if other categories are present
      finalCategories = newCategories.filter(cat => cat !== 'NONE');
    } else if (newCategories.length === 0) {
      // If no categories selected, default to "NONE"
      finalCategories = ['NONE'];
    }
    
    console.log('Final categories for page', pageId, ':', finalCategories);
    
    setPageCategories(prev => ({
      ...prev,
      [pageId]: finalCategories
    }));
  };

  const handleContinueWithCategories = async () => {
    handleNext();
    // Fetch optimal page ID for the review step
    await fetchOptimalPageId();
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    // Clear validation when going back
    if (activeStep === 3) {
      setValidationResult(null);
    }
  };

  // Fetch optimal page ID when moving to review step
  const fetchOptimalPageId = async () => {
    setLoadingOptimalPage(true);
    try {
      console.log('📊 Fetching optimal Facebook page ID for review...');
      const pageResponse = await Api.getOptimalPageId();
      if (pageResponse.success && pageResponse.data.optimal_page_id) {
        setOptimalPageId(pageResponse.data.optimal_page_id);
        setOptimalPageName(pageResponse.data.page_name);
        setPageLoadStats(pageResponse.data.all_pages_stats);
        console.log('✅ Preview: optimal page ID:', pageResponse.data.optimal_page_id, `(${pageResponse.data.page_name})`, 'with', pageResponse.data.running_ads_count, 'running ads');
      } else {
        console.warn('⚠️ Could not get optimal page ID for preview - no pages available');
        setOptimalPageId(null);
        setOptimalPageName('No Facebook page available');
      }
    } catch (error) {
      console.warn('⚠️ Error fetching optimal page ID for preview:', error);
      setOptimalPageId(null);
      setOptimalPageName('Error loading Facebook page');
    } finally {
      setLoadingOptimalPage(false);
    }
  };

  const validateCampaigns = async () => {
    setIsValidating(true);
    try {
      console.log('🔍 Validating campaign requirements...');
      
      const validationData = {
        account: selectedAccount,
        pages: selectedPages,
        pageCategories: pageCategories
      };
      
      const validation = await Api.validateCampaignRequirements(validationData);
      setValidationResult(validation);
      
      if (!validation.success) {
        onError(`Validation failed: ${validation.error}`);
        return false;
      }
      
      if (validation.warnings && validation.warnings.length > 0) {
        console.warn('⚠️ Validation warnings:', validation.warnings);
      }
      
      return true;
    } catch (error) {
      console.error('❌ Validation error:', error);
      onError(`Validation error: ${error.message}`);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handleQuickLaunch = async () => {
    setLoading(true);
    onError(null);
    
    try {
      // Step 0: Validate campaign requirements first
      const isValid = await validateCampaigns();
      if (!isValid) {
        setLoading(false);
        return;
      }
      
      console.log('🚀 Starting Quick Launch with:', {
        account: selectedAccount?.name,
        accountObject: selectedAccount,
        pages: selectedPages?.map(p => p.title)
      });

      // Step 1: Get optimal Facebook page ID for load balancing
      console.log('📊 Fetching optimal Facebook page ID...');
      let selectedOptimalPageId = null;
      let pageStats = {};
      
      try {
        const pageResponse = await Api.getOptimalPageId();
        if (pageResponse.success && pageResponse.data.optimal_page_id) {
          selectedOptimalPageId = pageResponse.data.optimal_page_id;
          pageStats = pageResponse.data.all_pages_stats;
          setOptimalPageId(selectedOptimalPageId);
          setOptimalPageName(pageResponse.data.page_name);
          setPageLoadStats(pageStats);
          console.log('✅ Selected optimal page ID:', selectedOptimalPageId, `(${pageResponse.data.page_name})`, 'with', pageResponse.data.running_ads_count, 'running ads');
          console.log('📈 All pages stats:', pageStats);
        } else {
          throw new Error('No optimal page ID returned from API');
        }
      } catch (error) {
        console.error('❌ Error fetching optimal page ID:', error);
        throw new Error('Failed to get optimal Facebook page ID. Please ensure you have Facebook pages configured and accessible.');
      }

      // Ensure we have a valid page ID before proceeding
      if (!selectedOptimalPageId) {
        throw new Error('No Facebook page ID available. Please ensure you have Facebook pages configured.');
      }

      // Initialize progress tracking
      const initialProgress = selectedPages.map(page => ({
        pageId: page.id,
        pageTitle: page.title,
        status: 'pending', // pending, processing, success, error
        result: null,
        error: null
      }));
      setCampaignProgress(initialProgress);

      // Process each page in parallel
      const campaignPromises = selectedPages.map(async (selectedPage, pageIndex) => {
        try {
          // Update progress to processing
          setCampaignProgress(prev => 
            prev.map(p => p.pageId === selectedPage.id 
              ? { ...p, status: 'processing' } 
              : p
            )
          );

          // Step 2: Fetch 4 diverse images (one from each category)
          const preloadedImages = await fetchPreloadedImages(selectedPage.slug, true);
          if (preloadedImages.length < 4) {
            throw new Error(`Not enough images for Quick Launch. Found ${preloadedImages.length} images, but need at least 4 diverse images. Please generate more images for this page first.`);
          }

          // Step 3: Generate campaign name with Widget MARIO prefix
          const campaignName = `Widget MARIO - ${selectedPage.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')} - ${new Date().toISOString().split('T')[0]}`;

          // Step 4: Generate ad copy for each image
          const adVariations = [];
          for (let i = 0; i < Math.min(preloadedImages.length, 4); i++) {
            try {
              const copy = await generateAdCopy({
                page: selectedPage,
                objective: 'OUTCOME_SALES',
                variationIndex: i + 1,
              });

              adVariations.push({
                copy,
                image: preloadedImages[i],
                variationId: `var_${i + 1}`,
              });
            } catch (error) {
              console.error(`Error generating copy for variation ${i + 1}:`, error);
              // Use a basic copy if generation fails
              adVariations.push({
                copy: {
                  headline: selectedPage.title.substring(0, 40),
                  primaryText: `Learn more about ${selectedPage.title}`,
                  description: 'Click to learn more'
                },
                image: preloadedImages[i],
                variationId: `var_${i + 1}`,
              });
            }
          }

          // Step 5: Build campaign data with defaults
          const campaignData = {
            campaign: {
              name: campaignName,
              objective: 'OUTCOME_SALES',
              status: 'ACTIVE',
              account_id: selectedAccount.id,
              page_id: String(selectedOptimalPageId), // Use optimal page ID with fewest running ads
              daily_budget: 10, // Default budget
              special_ad_categories: (pageCategories[selectedPage.id] || ['NONE']).filter(cat => cat !== 'NONE'),
              facebookPageId: String(selectedOptimalPageId), // Use optimal page ID with fewest running ads
              startTiming: 'delayed', // Always use delayed start for quick launch
              start_time: (() => {
                // Set to next day at midnight UTC
                const nowUTC = new Date();
                const tomorrowUTC = new Date(nowUTC.getTime() + (24 * 60 * 60 * 1000));
                tomorrowUTC.setUTCHours(0, 0, 0, 0);
                return tomorrowUTC.toISOString();
              })(),
            },
            adSet: {
              name: `${campaignName} - AdSet`,
              status: 'ACTIVE',
              optimization_goal: 'OFFSITE_CONVERSIONS',
              billing_event: 'IMPRESSIONS',
              targeting: {
                countries: ['US'], // Default to US
                interests: ['all'], // Required field
                ages: [18, 65],
                genders: ['male', 'female']
              }
            },
            ads: adVariations,
            selectedAccounts: [selectedAccount],
            selectedPage: selectedPage
          };

          // Step 6: Create the Facebook campaign
          console.log(`📤 Creating Facebook campaign for page: ${selectedPage.title}`);
          
          // Format ads correctly for the API
          const pageSlug = selectedPage.slug || selectedPage.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          
          // Handle keywords - they might be array, string, or null
          let keywordsArray = [];
          if (Array.isArray(selectedPage.keywords)) {
            keywordsArray = selectedPage.keywords;
          } else if (typeof selectedPage.keywords === 'string' && selectedPage.keywords.trim()) {
            keywordsArray = selectedPage.keywords.split(',').map(k => k.trim()).filter(k => k);
          } else {
            keywordsArray = [pageSlug];
          }
          
          const mainKeyword = keywordsArray.length > 0 ? keywordsArray[0] : pageSlug;
          const encodedKeywords = encodeURIComponent(keywordsArray.join(','));
          const hasValidKeywords = keywordsArray.length > 0 && keywordsArray[0] !== pageSlug;
          
          const formattedAds = adVariations.map(ad => ({
            name: `${campaignName} - ${ad.variationId}`,
            status: 'PAUSED',
            creative: {
              title: ad.copy.headline,
              body: ad.copy.primaryText,
              description: ad.copy.description,
              image_url: ad.image.url,
              link_destination_display_url: hasValidKeywords
                ? `www.thecozyguide.com/dsr/${mainKeyword}/`
                : `www.thecozyguide.com/dsr/${pageSlug}/`,
            },
            page_slug: pageSlug,
            destination_url: `https://www.thecozyguide.com/dsr/${pageSlug}/?adx_title=${encodeURIComponent(ad.copy.headline)}&adx_publisher_id=68&utm_source=facebook&adid={{ad.id}}&adx_terms=${encodedKeywords}&adx_lang=en&cmpgid={{campaign.id}}`
          }));
          
          const response = await Api.createCompleteFacebookCampaign({
            campaign: {
              name: campaignData.campaign.name,
              objective: campaignData.campaign.objective,
              status: campaignData.campaign.status,
              account_id: selectedAccount.id,
              page_id: String(campaignData.campaign.page_id),
              daily_budget: campaignData.campaign.daily_budget,
              special_ad_categories: campaignData.campaign.special_ad_categories,
              start_time: campaignData.campaign.start_time,
            },
            adSet: campaignData.adSet,
            ads: formattedAds,
            selectedPage: selectedPage
          });

          if (response.success) {
            // Update rsoc_spy campaign_id if available
            if (response.data?.campaign?.id && selectedPage?.id) {
              try {
                await Api.updateRsocSpyCampaignId(selectedPage.id, response.data.campaign.id);
                console.log('✅ Updated rsoc_spy campaign_id:', selectedPage.id, response.data.campaign.id);
              } catch (updateError) {
                console.warn('⚠️ Failed to update rsoc_spy campaign_id:', updateError);
                // Don't fail the whole operation if this update fails
              }
            }

            // Update progress to success
            setCampaignProgress(prev =>
              prev.map(p => p.pageId === selectedPage.id
                ? { ...p, status: 'success', result: response.data }
                : p
              )
            );
            return { pageId: selectedPage.id, success: true, data: response.data };
          } else {
            throw new Error(response.error || 'Failed to create campaign');
          }

        } catch (error) {
          console.error(`Campaign creation failed for page ${selectedPage.title}:`, error);
          // Update progress to error
          setCampaignProgress(prev => 
            prev.map(p => p.pageId === selectedPage.id 
              ? { ...p, status: 'error', error: error.message } 
              : p
            )
          );
          return { pageId: selectedPage.id, success: false, error: error.message };
        }
      });

      // Wait for all campaigns to complete
      const results = await Promise.all(campaignPromises);
      
      // Check results
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      if (successCount > 0) {
        if (failCount > 0) {
          onSuccess(`🎉 ${successCount} campaign(s) created successfully! ${failCount} failed.`);
        } else {
          onSuccess(`🎉 All ${successCount} campaigns created successfully!`);
        }
        handleNext(); // Move to results step
      } else {
        throw new Error('All campaigns failed to create');
      }

    } catch (error) {
      console.error('Multi-page Quick Launch failed:', error);
      onError(`Multi-page Quick Launch failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Select Account
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Facebook Account
            </Typography>
            <Typography color="text.secondary" paragraph>
              Choose the Facebook account to create your campaign in.
            </Typography>
            <AccountSelector
              accounts={facebookAccounts}
              loading={accountsLoading}
              error={accountsError}
              onAccountSelect={handleAccountSelect}
              selectedAccounts={selectedAccount ? [selectedAccount] : []}
              multiSelect={false}
            />
          </Box>
        );

      case 1: // Select Pages
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Content Pages
            </Typography>
            <Typography color="text.secondary" paragraph>
              Choose one or more RSOC pages to create campaigns for. Each page will become a separate campaign.
            </Typography>
            
            <PageSelector
              onPageSelect={handlePageSelect}
              selectedPages={selectedPages}
              quickLaunchMode={true}
              enableSearch={true}
              multiSelect={true}
              useRsocSpy={true}
            />
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {selectedPages.length} page(s) selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button 
                  variant="contained" 
                  onClick={handleContinueWithPages}
                  disabled={selectedPages.length === 0}
                >
                  Continue with {selectedPages.length} page(s)
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 2: // Configure Categories
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure Special Categories
            </Typography>
            <Typography color="text.secondary" paragraph>
              Assign special ad categories to each page. Most pages use "None" unless they contain specific content types.
            </Typography>
            
            {selectedPages.map((page, index) => (
              <Card key={page.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>
                      {page.title}
                    </Typography>
                    <Chip label={`Page ${index + 1}`} size="small" variant="outlined" />
                  </Box>
                  
                  <FormControl fullWidth>
                    <InputLabel id={`special-categories-${page.id}-label`}>
                      Special Ad Categories
                    </InputLabel>
                    <Select
                      labelId={`special-categories-${page.id}-label`}
                      id={`special-categories-${page.id}-select`}
                      multiple
                      value={pageCategories[page.id] || ['NONE']}
                      onChange={(event) => handleCategoriesChange(page.id, event.target.value)}
                      label="Special Ad Categories"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={specialAdCategories.find(cat => cat.value === value)?.label || value} 
                              size="small"
                              color={value === 'NONE' ? 'default' : 'primary'}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {specialAdCategories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          <Checkbox checked={(pageCategories[page.id] || ['NONE']).indexOf(category.value) > -1} />
                          <ListItemText 
                            primary={category.label} 
                            secondary={category.description}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            ))}
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {selectedPages.length} page(s) configured
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button 
                  variant="contained" 
                  onClick={handleContinueWithCategories}
                >
                  Continue to Launch
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 3: // Launch Campaigns
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Ready to Launch {selectedPages.length} Campaign(s)
            </Typography>
            <Typography color="text.secondary" paragraph>
              Review your selection and launch campaigns with these automatic settings:
            </Typography>
            
            {/* Selected Pages Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Selected Pages ({selectedPages.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedPages.map(page => (
                    <Chip 
                      key={page.id} 
                      label={page.title} 
                      size="small" 
                      variant="outlined"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Campaign Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Account: {selectedAccount?.name}<br/>
                      • Campaigns: {selectedPages.length}<br/>
                      • Objective: Sales<br/>
                      • Budget: $10/day each<br/>
                      • Start: Tomorrow at midnight<br/>
                      • Targeting: US, Ages 18-65<br/>
                      {loadingOptimalPage ? (
                        <>
                          • FB Page: <CircularProgress size={12} sx={{ ml: 1 }} /> Selecting optimal page...<br/>
                          • Selection: Checking Facebook API...
                        </>
                      ) : optimalPageId ? (
                        <>
                          • FB Page: {optimalPageName || optimalPageId}<br/>
                          • Page ID: {optimalPageId}<br/>
                          • Selection: {Object.keys(pageLoadStats).length > 1 
                            ? `Auto (${typeof pageLoadStats[optimalPageId] === 'object' 
                                ? pageLoadStats[optimalPageId]?.running_ads || 0 
                                : pageLoadStats[optimalPageId] || 0} running ads - lowest)`
                            : 'Default (single page)'
                          }
                        </>
                      ) : (
                        <>• FB Page: Default ({selectedAccount?.name})</>
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Ad Creative
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • 4 ad variations per campaign<br/>
                      • One from each image style<br/>
                      • AI-generated copy<br/>
                      • Optimized for conversions<br/>
                      • Names: MARIO - [page-name]<br/>
                      • Total ads: {selectedPages.length * 4}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Special Categories
                    </Typography>
                    <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                      {selectedPages.map(page => {
                        const categories = pageCategories[page.id] || ['NONE'];
                        const categoryLabels = categories.map(cat => 
                          specialAdCategories.find(c => c.value === cat)?.label || cat
                        );
                        return (
                          <Typography key={page.id} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>{page.title.substring(0, 20)}:</strong> {categoryLabels.join(', ')}
                          </Typography>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Validation Results */}
            {validationResult && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Pre-Launch Validation
                  </Typography>
                  
                  {validationResult.success ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      ✅ All requirements validated successfully
                    </Alert>
                  ) : (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      ❌ Validation failed: {validationResult.error}
                    </Alert>
                  )}
                  
                  {validationResult.warnings && validationResult.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" component="div">
                        <strong>Warnings:</strong>
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                          {validationResult.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                          ))}
                        </ul>
                      </Typography>
                    </Alert>
                  )}
                  
                  {validationResult.details && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Validation Details:</strong><br/>
                        • Facebook API: {validationResult.details.facebook_api_available ? '✅ Available' : '❌ Not available'}<br/>
                        • Optimal Page: {validationResult.details.optimal_page_available ? '✅ Available' : '❌ Not available'}<br/>
                        • Pages Validated: {validationResult.details.pages_validated || 0} / {selectedPages.length}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Progress Display During Creation */}
            {loading && campaignProgress.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Campaign Creation Progress
                  </Typography>
                  {campaignProgress.map(progress => (
                    <Box key={progress.pageId} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ minWidth: 35 }}>
                        {progress.status === 'pending' && '⏳'}
                        {progress.status === 'processing' && <CircularProgress size={16} />}
                        {progress.status === 'success' && '✅'}
                        {progress.status === 'error' && '❌'}
                      </Box>
                      <Typography variant="body2" sx={{ flex: 1, ml: 1 }}>
                        {progress.pageTitle}
                      </Typography>
                      {progress.status === 'error' && (
                        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                          {progress.error}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button onClick={handleBack} disabled={loading || isValidating}>
                Back
              </Button>
              {!validationResult && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={validateCampaigns}
                  disabled={loading || loadingOptimalPage || isValidating}
                  sx={{ minWidth: 250 }}
                >
                  {isValidating ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Validating...
                    </>
                  ) : (
                    `🔍 Validate ${selectedPages.length} Campaign(s)`
                  )}
                </Button>
              )}
              {validationResult && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleQuickLaunch}
                  disabled={loading || loadingOptimalPage || !validationResult.success}
                  sx={{ minWidth: 250 }}
                >
                  {loadingOptimalPage ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Selecting Page...
                    </>
                  ) : loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Creating {selectedPages.length} Campaign(s)...
                    </>
                  ) : (
                    `🚀 Launch ${selectedPages.length} Campaign(s)`
                  )}
                </Button>
              )}
            </Box>
          </Box>
        );

      case 4: // Results
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="success.main">
              🎉 Campaign Creation Complete!
            </Typography>
            
            {/* Results Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {campaignProgress.filter(p => p.status === 'success').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Successful
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {campaignProgress.filter(p => p.status === 'error').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Failed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main">
                      {campaignProgress.filter(p => p.status === 'success').length * 4}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Ads
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* Detailed Results */}
            <Box sx={{ textAlign: 'left', mb: 3 }}>
              {campaignProgress.map(progress => (
                <Card key={progress.pageId} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ mr: 2 }}>
                        {progress.status === 'success' && '✅'}
                        {progress.status === 'error' && '❌'}
                      </Box>
                      <Typography variant="h6" sx={{ flex: 1 }}>
                        {progress.pageTitle}
                      </Typography>
                      {progress.status === 'success' && (
                        <Chip label="Success" color="success" size="small" />
                      )}
                      {progress.status === 'error' && (
                        <Chip label="Failed" color="error" size="small" />
                      )}
                    </Box>
                    
                    {progress.status === 'success' && progress.result && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Campaign ID:</strong> {progress.result.campaign?.id}<br/>
                            <strong>Name:</strong> {progress.result.campaign?.name}<br/>
                            <strong>Budget:</strong> $10/day
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Ad Set:</strong> {progress.result.adSet?.name}<br/>
                            <strong>Ads Created:</strong> {progress.result.ads?.length || 0}<br/>
                            <strong>Start Time:</strong> Tomorrow at midnight UTC
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                    
                    {progress.status === 'error' && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <strong>Error:</strong> {progress.error}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
            
            <Button
              variant="outlined"
              onClick={() => {
                setActiveStep(0);
                setSelectedAccount(null);
                setSelectedPages([]);
                setPageCategories({});
                setCampaignResult(null);
                setCampaignProgress([]);
                setValidationResult(null);
                setOptimalPageId(null);
                setOptimalPageName(null);
                setPageLoadStats({});
              }}
              sx={{ mt: 3 }}
            >
              Launch More Campaigns
            </Button>
          </Box>
        );

      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Paper 
      sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 3, 
        borderRadius: 2,
        boxShadow: 2
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          🚀 Quick Launch
        </Typography>
        <Typography color="text.secondary">
          Automatically create campaigns with 4 diverse ad variations using preloaded images.
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {quickSteps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 2, mb: 2 }}>
        {renderStepContent()}
      </Box>
    </Paper>
  );
};

export default QuickLauncher;